import React from 'react';

import {useIntl} from 'react-intl';
import styled from 'styled-components';

import {dimensions} from '@stryd/react-ui';

import {usePageRegionData} from 'src/contexts';
import {VariantPricing} from 'src/features/pricing/helpers';
import {MoneyBag} from 'src/features/pricing/types';
import {usePriceFormatters} from 'src/hooks/usePriceFormatters';

const columnLayoutBreakpoint = dimensions.breakpoints.tabletLandscape;

interface ProductOption {
  id: string;
  name: string;
  description: string[];
  included: string;
  isSelected: boolean;
  accent: string;
  onClick: () => void;
}

interface ProductSelectorProps {
  products: ProductOption[];
  productPrices: {
    [key: string]: {
      [key: string]: VariantPricing;
    };
  };
  selectedProduct: string;
  selectedCommitment: string;
  className?: string;
}

const SelectorContainer = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 12px;
  box-sizing: border-box;

  @media screen and (min-width: ${columnLayoutBreakpoint}) {
    width: 100%;
    padding: 30px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`;

const SelectorTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 16px;
  color: #1a1a1a;

  @media screen and (min-width: ${columnLayoutBreakpoint}) {
    font-size: 24px;
  }
`;

const ProductOptions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  max-width: 100%;

  @media screen and (max-width: ${columnLayoutBreakpoint}) {
    overflow-x: auto;
    padding-bottom: 8px; /* Add some padding to account for scrollbar */
  }
`;

const ProductOption = styled.button<{isSelected: boolean}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px;
  border: 2px solid ${(props) => (props.isSelected ? '#41a4ff' : '#e0e0e0')};
  border-radius: 12px;
  background: ${(props) => (props.isSelected ? '#f0f8ff' : '#fff')};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  width: 100%;

  @media screen and (max-width: ${columnLayoutBreakpoint}) {
    flex: 0 0 280px;
    min-width: 280px;
  }

  &:hover {
    border-color: ${(props) => (props.isSelected ? '#41a4ff' : '#b0b0b0')};
    background: ${(props) => (props.isSelected ? '#f0f8ff' : '#f8f8f8')};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(65, 164, 255, 0.2);
  }
`;

const ProductNameOld = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #1a1a1a;
`;

const ProductDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 12px;
  line-height: 1.4;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  font-size: 14px;
  color: #666;
  line-height: 1.4;
`;

const CheckmarkIcon = styled.span`
  color: #41a4ff;
  margin-right: 8px;
  font-weight: bold;
  flex-shrink: 0;
  margin-top: 1px;
`;

const ProductPricingSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 10px;
  text-align: center;
`;

const ProductName = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #000;
  margin: 0 0 8px 0;
  text-align: center;
`;

const PricingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

const CurrentPrice = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #000;
`;

const OldPrice = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: #999;
  text-decoration: line-through;
`;

const SavingsBadge = styled.div`
  background: #f4e4c1;
  color: #8b4513;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
`;

const ProductPrice = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #41a4ff;
`;

const ProductSavings = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #41a4ff;
`;

const AccentBadge = styled.div`
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  background: #41a4ff;
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 10;
  white-space: nowrap;

  @media screen and (max-width: ${columnLayoutBreakpoint}) {
    position: relative;
    top: 0;
    left: 0;
    transform: none;
    margin-bottom: 8px;
    display: inline-block;
  }
`;

const SelectedIndicator = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #41a4ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: bold;
`;

const ProductSelector: React.FC<ProductSelectorProps> = ({
  products,
  className,
  productPrices,
  selectedProduct,
  selectedCommitment,
}) => {
  const {locale} = usePageRegionData();
  const {formatOneTimePrice} = usePriceFormatters({
    locale,
  });
  const intl = useIntl();
  const selectedProductOption = products.find((p) => p.isSelected);

  const selectedProductPrice =
    productPrices[selectedProduct][selectedCommitment];

  let productSavings = '';
  let priceTotal = '';
  if (
    selectedCommitment === 'monthly-commitment' ||
    selectedCommitment === 'yearly-upfront'
  ) {
    const priceDifferenceMonthly: MoneyBag = {
      ...productPrices[selectedProduct]['no-membership'].oneTimePricesTotal,
      unit_amount:
        productPrices[selectedProduct]['no-membership'].oneTimePricesTotal
          .unit_amount - selectedProductPrice.oneTimePricesTotal.unit_amount,
    };
    productSavings = intl.formatMessage(
      {id: 'store5.productSavings'},
      {
        savings: formatOneTimePrice(priceDifferenceMonthly),
        productName: selectedProductOption?.name,
      }
    );
    const priceDifferenceTotal: MoneyBag = {
      ...productPrices[selectedProduct]['no-membership'].oneTimePricesTotal,
      unit_amount:
        productPrices[selectedProduct]['no-membership'].oneTimePricesTotal
          .unit_amount,
    };
    priceTotal = formatOneTimePrice(priceDifferenceTotal);
  }

  return (
    <SelectorContainer className={className}>
      <SelectorTitle>
        {intl.formatMessage({id: 'store5.productTitle'})}
      </SelectorTitle>
      {/* <SelectorDescription>
        Select the perfect Stryd package for your running journey
      </SelectorDescription> */}
      <ProductPricingSection>
        <ProductName>{selectedProductOption?.name}</ProductName>
        <PricingContainer>
          <CurrentPrice>
            {formatOneTimePrice(selectedProductPrice.oneTimePricesTotal)}
          </CurrentPrice>
          {priceTotal != '' && <OldPrice>{priceTotal}</OldPrice>}
          {productSavings != '' && (
            <SavingsBadge>{productSavings}</SavingsBadge>
          )}
        </PricingContainer>
      </ProductPricingSection>

      <ProductOptions>
        {products.map((product) => (
          <ProductOption
            key={product.id}
            isSelected={product.isSelected}
            onClick={product.onClick}
            style={{position: 'relative'}}
          >
            {product.accent && product.accent.trim() !== '' && (
              <AccentBadge>{product.accent}</AccentBadge>
            )}
            {product.isSelected && <SelectedIndicator>✓</SelectedIndicator>}
            <ProductNameOld>{product.name}</ProductNameOld>
            <ProductDescription>
              <FeatureList>
                {product.description.map((feature, index) => (
                  <FeatureItem key={index}>
                    <CheckmarkIcon>✓</CheckmarkIcon>
                    <span>{feature}</span>
                  </FeatureItem>
                ))}
              </FeatureList>
            </ProductDescription>
            {/* <ProductPrice>{product.price}</ProductPrice> */}
          </ProductOption>
        ))}
      </ProductOptions>
    </SelectorContainer>
  );
};

export default ProductSelector;
